import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { ListFooter } from '../ui';

// Selfie files are served at /api/v1/files/<name>; prefix with the API origin in dev.
const FILE_ORIGIN = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3000';
const fileSrc = (p) => (p ? FILE_ORIGIN + p : null);
const NAVY = '#3B3A8F', RED = '#E22D2D', GREEN = '#16a34a', WARM = '#F7F6F3';
const card = { background: '#fff', borderRadius: 12, border: '1px solid #E8E7E7', padding: 18, marginBottom: 16 };
const input = { padding: '9px 11px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, width: '100%', boxSizing: 'border-box' };
const label = { fontSize: 12, color: '#666', fontWeight: 600, display: 'block', marginBottom: 4 };
const btn = (bg) => ({ padding: '10px 18px', background: bg, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' });
const fmtT = (ts) => ts ? new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—';
const fmtDT = (ts) => ts ? new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

// Promise wrapper for browser geolocation; resolves {lat,lng} or null (denied/unavailable).
const getGeo = () => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve(null);
  navigator.geolocation.getCurrentPosition(
    (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
    () => resolve(null),
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
});

export default function FieldAttendance() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isHr = ['admin', 'management', 'hr', 'auditor'].includes(user?.role);
  const [tab, setTab] = useState('punch');
  const tabs = [['punch', '📍 Punch'], ...(isAdmin ? [['locations', '🗺️ Allowed Locations']] : []), ...(isHr ? [['review', '🧾 Review']] : [])];
  return (
    <div style={{ minHeight: '100vh', background: 'transparent' }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: 20 }}>
        <h2 style={{ color: NAVY, margin: '0 0 14px' }}>Field Attendance</h2>
        {tabs.length > 1 && (
          <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #eee', flexWrap: 'wrap' }}>
            {tabs.map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} style={{ padding: '9px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, fontWeight: tab === k ? 700 : 500, color: tab === k ? NAVY : '#888', borderBottom: tab === k ? `3px solid ${NAVY}` : '3px solid transparent' }}>{l}</button>
            ))}
          </div>
        )}
        {tab === 'punch' && <PunchTab />}
        {tab === 'locations' && isAdmin && <LocationsTab />}
        {tab === 'review' && isHr && <ReviewTab />}
      </div>
    </div>
  );
}

// ─── PUNCH (mobile field worker) ───────────────────────────────────────────────
function PunchTab() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const fileRef = useRef(null);
  const pendingType = useRef(null);

  const load = useCallback(() => api.get('/hr/geo/my-status').then((r) => setStatus(r.data.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const startPunch = (type) => { pendingType.current = type; setMsg(null); if (fileRef.current) { fileRef.current.value = ''; fileRef.current.click(); } };

  const onSelfie = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setBusy(true); setMsg(null);
    try {
      const geo = await getGeo();           // null if denied → punch still recorded (flagged)
      const fd = new FormData();
      fd.append('punch_type', pendingType.current);
      fd.append('selfie', file);
      if (geo) { fd.append('latitude', geo.lat); fd.append('longitude', geo.lng); }
      const r = await api.post('/hr/geo/punch', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const p = r.data.data;
      const where = p.within_geofence ? (p.matched_location_name || 'allowed location') : (geo ? 'outside allowed locations — flagged for review' : 'location not shared — flagged for review');
      setMsg({ ok: true, text: `Punched ${p.punch_type.toUpperCase()} at ${fmtT(p.punched_at)} · ${where}` });
      load();
    } catch (err) { setMsg({ ok: false, text: err.response?.data?.error || err.message }); }
    finally { setBusy(false); }
  };

  if (status && status.linked === false) return <div style={card}>Your login isn’t linked to an employee record yet. Ask HR to link it, then you can punch.</div>;
  const punches = status?.punches || [];
  const lastIn = [...punches].reverse().find((p) => p.punch_type === 'in');
  const lastOut = [...punches].reverse().find((p) => p.punch_type === 'out');

  return (
    <div>
      <div style={{ ...card, textAlign: 'center' }}>
        <div style={{ fontSize: 13, color: '#888' }}>Hello{status?.employee_name ? `, ${status.employee_name}` : ''}</div>
        <div style={{ fontSize: 12, color: '#aaa', marginBottom: 14 }}>Today · {status?.today || ''}</div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button disabled={busy} onClick={() => startPunch('in')} style={{ ...btn(GREEN), minWidth: 150, padding: '18px 20px', fontSize: 18, opacity: busy ? 0.6 : 1 }}>🟢 Punch In</button>
          <button disabled={busy} onClick={() => startPunch('out')} style={{ ...btn(RED), minWidth: 150, padding: '18px 20px', fontSize: 18, opacity: busy ? 0.6 : 1 }}>🔴 Punch Out</button>
        </div>
        <input ref={fileRef} type="file" accept="image/*" capture="user" onChange={onSelfie} style={{ display: 'none' }} />
        <div style={{ fontSize: 11, color: '#aaa', marginTop: 12 }}>{busy ? 'Capturing location & submitting…' : 'Tap a button → take a selfie → location is captured automatically.'}</div>
        {msg && <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: msg.ok ? '#DDF3E4' : '#FBE2E2', color: msg.ok ? '#0F7B3F' : RED }}>{msg.text}</div>}
      </div>

      <div style={card}>
        <div style={{ fontWeight: 700, color: NAVY, marginBottom: 6 }}>Today’s punches</div>
        <div style={{ display: 'flex', gap: 20, marginBottom: 10, fontSize: 13 }}>
          <div>First In: <b style={{ color: GREEN }}>{fmtT(lastIn?.punched_at) === '—' ? '—' : fmtT(punches.find((p) => p.punch_type === 'in')?.punched_at)}</b></div>
          <div>Last Out: <b style={{ color: RED }}>{fmtT(lastOut?.punched_at)}</b></div>
        </div>
        {punches.length === 0 ? <div style={{ color: '#aaa', fontSize: 13 }}>No punches yet today.</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: WARM, color: '#666' }}>{['Type', 'Time', 'Location', 'Selfie'].map((h) => <th key={h} style={{ padding: '6px 8px', textAlign: 'left' }}>{h}</th>)}</tr></thead>
            <tbody>
              {punches.map((p) => (
                <tr key={p.id} style={{ borderBottom: '1px solid #f3f3f3' }}>
                  <td style={{ padding: '6px 8px', fontWeight: 700, color: p.punch_type === 'in' ? GREEN : RED }}>{p.punch_type.toUpperCase()}</td>
                  <td style={{ padding: '6px 8px' }}>{fmtT(p.punched_at)}</td>
                  <td style={{ padding: '6px 8px' }}>{p.within_geofence ? (p.matched_location_name || '✓') : <span style={{ color: '#9A6B00' }}>⚑ flagged</span>}</td>
                  <td style={{ padding: '6px 8px' }}>{p.selfie_path ? <a href={fileSrc(p.selfie_path)} target="_blank" rel="noreferrer"><img src={fileSrc(p.selfie_path)} alt="selfie" style={{ height: 34, width: 34, objectFit: 'cover', borderRadius: 6 }} /></a> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {punches.length > 0 && <ListFooter shown={punches.length} total={punches.length} noun="punches" />}
      </div>
    </div>
  );
}

// ─── ALLOWED LOCATIONS (admin) ─────────────────────────────────────────────────
function LocationsTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: '', latitude: '', longitude: '', radius_meters: 150 });
  const [editId, setEditId] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => api.get('/hr/geo/locations').then((r) => setRows(r.data.data || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const capture = async () => {
    setErr('');
    const geo = await getGeo();
    if (!geo) { setErr('Could not read your location (permission denied or unavailable).'); return; }
    setForm((f) => ({ ...f, latitude: geo.lat.toFixed(7), longitude: geo.lng.toFixed(7) }));
  };
  const save = async () => {
    setErr('');
    if (!form.name || form.latitude === '' || form.longitude === '') { setErr('Name, latitude and longitude are required.'); return; }
    try {
      if (editId) await api.patch('/hr/geo/locations/' + editId, form);
      else await api.post('/hr/geo/locations', form);
      setForm({ name: '', latitude: '', longitude: '', radius_meters: 150 }); setEditId(null); load();
    } catch (e) { setErr(e.response?.data?.error || e.message); }
  };
  const toggle = async (l) => { try { await api.patch('/hr/geo/locations/' + l.id, { is_active: !l.is_active }); load(); } catch (e) { setErr(e.response?.data?.error || e.message); } };

  return (
    <div>
      <div style={card}>
        <div style={{ fontWeight: 700, color: NAVY, marginBottom: 12 }}>{editId ? 'Edit location' : 'Add allowed location (geofence)'}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 10, alignItems: 'end' }}>
          <div><label style={label}>Name *</label><input style={input} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Head Office" /></div>
          <div><label style={label}>Latitude *</label><input style={input} value={form.latitude} onChange={(e) => setForm((f) => ({ ...f, latitude: e.target.value }))} placeholder="e.g. 21.170" /></div>
          <div><label style={label}>Longitude *</label><input style={input} value={form.longitude} onChange={(e) => setForm((f) => ({ ...f, longitude: e.target.value }))} placeholder="e.g. 72.831" /></div>
          <div><label style={label}>Radius (m)</label><input style={input} type="number" value={form.radius_meters} onChange={(e) => setForm((f) => ({ ...f, radius_meters: e.target.value }))} /></div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'center' }}>
          <button onClick={capture} style={{ ...btn('#f0f2f8'), color: NAVY, border: '1px solid #ddd' }}>📍 Capture current location</button>
          <button onClick={save} style={btn(NAVY)}>{editId ? 'Update' : '+ Add'}</button>
          {editId && <button onClick={() => { setEditId(null); setForm({ name: '', latitude: '', longitude: '', radius_meters: 150 }); }} style={{ ...btn('#eee'), color: '#555' }}>Cancel</button>}
          {err && <span style={{ color: RED, fontSize: 13, fontWeight: 600 }}>{err}</span>}
        </div>
      </div>
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ background: WARM, color: '#666' }}>{['Name', 'Latitude', 'Longitude', 'Radius', 'Active', ''].map((h) => <th key={h} style={{ padding: '8px 10px', textAlign: 'left' }}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No locations yet.</td></tr>
              : rows.map((l) => (
                <tr key={l.id} style={{ borderBottom: '1px solid #f3f3f3', opacity: l.is_active ? 1 : 0.5 }}>
                  <td style={{ padding: '8px 10px', fontWeight: 600 }}>{l.name}</td>
                  <td style={{ padding: '8px 10px', fontFamily: 'monospace' }}>{Number(l.latitude).toFixed(6)}</td>
                  <td style={{ padding: '8px 10px', fontFamily: 'monospace' }}>{Number(l.longitude).toFixed(6)}</td>
                  <td style={{ padding: '8px 10px' }}>{l.radius_meters} m</td>
                  <td style={{ padding: '8px 10px' }}>{l.is_active ? <span style={{ color: GREEN, fontWeight: 600 }}>● Active</span> : <span style={{ color: RED }}>○ Inactive</span>}</td>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                    <button onClick={() => { setEditId(l.id); setForm({ name: l.name, latitude: l.latitude, longitude: l.longitude, radius_meters: l.radius_meters }); setErr(''); }} style={{ padding: '4px 10px', background: '#FAEEDA', color: '#854F0B', border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer', marginRight: 4 }}>Edit</button>
                    <button onClick={() => toggle(l)} style={{ padding: '4px 10px', background: l.is_active ? '#fee2e2' : '#dcfce7', color: l.is_active ? RED : GREEN, border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{l.is_active ? 'Deactivate' : 'Activate'}</button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        <ListFooter shown={rows.length} total={rows.length} noun="locations" />
      </div>
    </div>
  );
}

// ─── REVIEW (admin/HR) ─────────────────────────────────────────────────────────
function ReviewTab() {
  const [rows, setRows] = useState([]);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const load = useCallback(() => api.get('/hr/geo/punches' + (flaggedOnly ? '?flagged=true' : '')).then((r) => setRows(r.data.data || [])).catch(() => {}), [flaggedOnly]);
  useEffect(() => { load(); }, [load]);
  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontWeight: 700, color: NAVY }}>Geo-selfie punches</div>
        <label style={{ fontSize: 13, color: '#555', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={flaggedOnly} onChange={(e) => setFlaggedOnly(e.target.checked)} /> Flagged only
        </label>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead><tr style={{ background: WARM, color: '#666' }}>{['Employee', 'Type', 'When', 'Location', 'Coords', 'Selfie', 'Flag'].map((h) => <th key={h} style={{ padding: '8px 10px', textAlign: 'left' }}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No punches.</td></tr>
            : rows.map((p) => (
              <tr key={p.id} style={{ borderBottom: '1px solid #f3f3f3', background: p.flagged ? '#FFF7ED' : 'transparent' }}>
                <td style={{ padding: '8px 10px', fontWeight: 600 }}>{p.employee_name} <span style={{ color: '#aaa', fontWeight: 400 }}>{p.employee_code}</span></td>
                <td style={{ padding: '8px 10px', fontWeight: 700, color: p.punch_type === 'in' ? GREEN : RED }}>{p.punch_type.toUpperCase()}</td>
                <td style={{ padding: '8px 10px' }}>{fmtDT(p.punched_at)}</td>
                <td style={{ padding: '8px 10px' }}>{p.within_geofence ? (p.matched_location_name || '✓ inside') : <span style={{ color: '#9A6B00', fontWeight: 600 }}>outside</span>}</td>
                <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 11 }}>{p.latitude != null ? `${Number(p.latitude).toFixed(5)}, ${Number(p.longitude).toFixed(5)}` : <span style={{ color: RED }}>no GPS</span>}</td>
                <td style={{ padding: '8px 10px' }}>{p.selfie_path ? <a href={fileSrc(p.selfie_path)} target="_blank" rel="noreferrer"><img src={fileSrc(p.selfie_path)} alt="selfie" style={{ height: 40, width: 40, objectFit: 'cover', borderRadius: 6 }} /></a> : '—'}</td>
                <td style={{ padding: '8px 10px' }}>{p.flagged ? <span style={{ fontSize: 11, fontWeight: 700, background: '#fee2e2', color: RED, padding: '2px 8px', borderRadius: 8 }}>⚑ REVIEW</span> : <span style={{ color: GREEN }}>ok</span>}</td>
              </tr>
            ))}
        </tbody>
      </table>
      <ListFooter shown={rows.length} total={rows.length} noun="punches" />
    </div>
  );
}
